/**
 * TaskCrewAssignment - Component for managing crew assignments on a task
 * PROMPT 34: Task-Level Crew Assignment (Foundations)
 * PROMPT 36: Seen vs Unseen State for Task Requests
 */
import { useState, useEffect, useCallback } from "react";
import {
  getTaskCrew,
  getEligibleCrew,
  assignCrewToTask,
  removeCrewFromTask,
  extractErrorMessage,
} from "../../lib/api";
import { useFormatters } from "../shared/useFormatters";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import { toast } from "sonner";
import {
  Users,
  UserPlus,
  X,
  Clock,
  AlertTriangle,
  CheckCircle2,
  Loader2,
  Info,
  Eye,
  EyeOff,
} from "lucide-react";
import { cn } from "../../lib/utils";

/**
 * Status badge component for assignment status
 */
function AssignmentStatusBadge({ status, rejectionReason }) {
  const statusConfig = {
    pending: {
      label: "Pending",
      className: "bg-yellow-500/10 text-yellow-600 border-yellow-500/30",
      icon: Clock,
    },
    accepted: {
      label: "Accepted",
      className: "bg-green-500/10 text-green-600 border-green-500/30",
      icon: CheckCircle2,
    },
    rejected: {
      label: "Rejected",
      className: "bg-red-500/10 text-red-600 border-red-500/30",
      icon: X,
    },
  };

  const config = statusConfig[status] || statusConfig.pending;
  const Icon = config.icon;

  const badge = (
    <Badge variant="outline" className={cn("text-xs gap-1", config.className)}>
      <Icon className="h-3 w-3" />
      {config.label}
    </Badge>
  );

  // Show tooltip with rejection reason if available
  if (status === "rejected" && rejectionReason) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            {badge}
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs">
            <p className="font-medium mb-1">Rejection reason:</p>
            <p className="text-sm">{rejectionReason}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return badge;
}

/**
 * Seen/Unseen indicator component
 * PROMPT 36: Seen vs Unseen State
 */
function SeenIndicator({ seenAt, formatDate }) {
  const isSeen = !!seenAt;
  
  const indicator = (
    <span
      className={cn(
        "flex items-center justify-center w-6 h-6 rounded-full",
        isSeen 
          ? "text-primary bg-primary/10" 
          : "text-muted-foreground/50 bg-surface-elevated/30"
      )}
      data-testid={isSeen ? "seen-indicator" : "unseen-indicator"}
    >
      {isSeen ? (
        <Eye className="h-3.5 w-3.5" />
      ) : (
        <EyeOff className="h-3.5 w-3.5" />
      )}
    </span>
  );

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          {indicator}
        </TooltipTrigger>
        <TooltipContent side="top">
          {isSeen ? (
            <p className="text-sm">Viewed on {formatDate(seenAt)}</p>
          ) : (
            <p className="text-sm">Not yet viewed</p>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/**
 * Assigned crew member row
 */
function AssignedCrewRow({ assignment, onRemove, removing, formatDate }) {
  return (
    <div
      className="flex items-center justify-between py-2 px-3 rounded-md bg-surface-elevated/30 border border-border/50"
      data-testid={`assigned-crew-${assignment.staff_id}`}
    >
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
          <Users className="h-4 w-4 text-primary" />
        </div>
        <div>
          <p className="text-sm font-medium">{assignment.staff_name}</p>
          <p className="text-xs text-muted-foreground">{assignment.staff_email}</p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <SeenIndicator seenAt={assignment.seen_at} formatDate={formatDate} />
        <AssignmentStatusBadge status={assignment.status} rejectionReason={assignment.rejection_reason} />
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-muted-foreground hover:text-destructive"
          onClick={() => onRemove(assignment.staff_id)}
          disabled={removing}
          data-testid={`remove-crew-${assignment.staff_id}`}
        >
          {removing ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <X className="h-4 w-4" />
          )}
        </Button>
      </div>
    </div>
  );
}

/**
 * Eligible staff select option
 */
function EligibleStaffOption({ staff }) {
  const isIneligible = !staff.is_eligible;

  return (
    <div className="flex items-center justify-between w-full">
      <span className={cn(isIneligible && "text-muted-foreground")}>
        {staff.first_name} {staff.last_name}
      </span>
      {isIneligible && staff.missing_skills?.length > 0 && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="flex items-center gap-1 text-xs text-amber-500">
                <AlertTriangle className="h-3 w-3" />
                Missing skills
              </span>
            </TooltipTrigger>
            <TooltipContent side="right" className="max-w-xs">
              <p className="font-medium mb-1">Missing required skills:</p>
              <ul className="list-disc list-inside text-xs">
                {staff.missing_skills.map((skill) => (
                  <li key={skill}>{skill}</li>
                ))}
              </ul>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
      {staff.is_eligible && (
        <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
      )}
    </div>
  );
}

/**
 * Main TaskCrewAssignment component
 */
export function TaskCrewAssignment({ shiftTaskId, readOnly = false }) {
  const { formatDate } = useFormatters();
  const [assignedCrew, setAssignedCrew] = useState([]);
  const [eligibleCrew, setEligibleCrew] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(false);
  const [removingId, setRemovingId] = useState(null);
  const [selectedStaffId, setSelectedStaffId] = useState("");

  // Load assigned and eligible crew
  const loadCrew = useCallback(async () => {
    if (!shiftTaskId) return;

    try {
      setLoading(true);
      const [assignedRes, eligibleRes] = await Promise.all([
        getTaskCrew(shiftTaskId),
        getEligibleCrew(shiftTaskId, true),
      ]);
      setAssignedCrew(assignedRes.data || []);
      setEligibleCrew(eligibleRes.data || []);
    } catch (err) {
      console.error("Failed to load crew:", extractErrorMessage(err));
      toast.error("Failed to load crew assignments");
    } finally {
      setLoading(false);
    }
  }, [shiftTaskId]);

  useEffect(() => {
    loadCrew();
  }, [loadCrew]);

  // Assign crew member
  const handleAssign = async () => {
    if (!selectedStaffId) return;

    try {
      setAssigning(true);
      await assignCrewToTask(shiftTaskId, selectedStaffId);
      toast.success("Crew member assigned");
      setSelectedStaffId("");
      await loadCrew();
    } catch (err) {
      toast.error(`Failed to assign: ${extractErrorMessage(err)}`);
    } finally {
      setAssigning(false);
    }
  };

  // Remove crew member
  const handleRemove = async (staffId) => {
    try {
      setRemovingId(staffId);
      await removeCrewFromTask(shiftTaskId, staffId);
      toast.success("Crew member removed");
      await loadCrew();
    } catch (err) {
      toast.error(`Failed to remove: ${extractErrorMessage(err)}`);
    } finally {
      setRemovingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-5 w-5 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="task-crew-assignment">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-primary" />
          <h4 className="font-medium text-sm">Assigned Crew</h4>
          {assignedCrew.length > 0 && (
            <Badge variant="secondary" className="text-xs">
              {assignedCrew.length}
            </Badge>
          )}
        </div>
      </div>

      {/* Assignment Section */}
      {!readOnly && (
        <div className="flex gap-2">
          <Select
            value={selectedStaffId}
            onValueChange={setSelectedStaffId}
            disabled={assigning}
          >
            <SelectTrigger
              className="flex-1"
              data-testid="crew-select"
            >
              <SelectValue placeholder="Select crew member to assign..." />
            </SelectTrigger>
            <SelectContent>
              {eligibleCrew.length === 0 ? (
                <div className="px-2 py-3 text-sm text-muted-foreground text-center">
                  <Info className="h-4 w-4 mx-auto mb-1" />
                  No eligible crew available
                </div>
              ) : (
                eligibleCrew.map((staff) => (
                  <SelectItem
                    key={staff.id}
                    value={staff.id}
                    disabled={!staff.is_eligible}
                    className={cn(!staff.is_eligible && "opacity-50")}
                  >
                    <EligibleStaffOption staff={staff} />
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
          <Button
            onClick={handleAssign}
            disabled={!selectedStaffId || assigning}
            className="gap-1"
            data-testid="assign-crew-btn"
          >
            {assigning ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <UserPlus className="h-4 w-4" />
            )}
            Assign
          </Button>
        </div>
      )}

      {/* Info about ineligible staff */}
      {!readOnly && eligibleCrew.some((s) => !s.is_eligible) && (
        <p className="text-xs text-muted-foreground flex items-center gap-1">
          <AlertTriangle className="h-3 w-3 text-amber-500" />
          Some crew members are ineligible due to missing required skills
        </p>
      )}

      {/* Assigned Crew List */}
      {assignedCrew.length === 0 ? (
        <div className="py-6 text-center border border-dashed border-border/50 rounded-md">
          <Users className="h-8 w-8 mx-auto text-muted-foreground/50 mb-2" />
          <p className="text-sm text-muted-foreground">No crew assigned yet</p>
          {!readOnly && (
            <p className="text-xs text-muted-foreground mt-1">
              Use the dropdown above to assign crew members
            </p>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {assignedCrew.map((assignment) => (
            <AssignedCrewRow
              key={assignment.id}
              assignment={assignment}
              onRemove={handleRemove}
              removing={removingId === assignment.staff_id}
              formatDate={formatDate}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default TaskCrewAssignment;
